import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { CardsApi, UsersApi } from '@shared/api/rest';
import type { CardResponse, CardsRequest } from '@shared/api/rest/cards';
import type { AddUserCardsRequest, DeleteUserCardsRequest } from '@shared/api/rest/users';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getUserCardsEffect = createEffect((params: IQueryParams<CardsRequest>) => CardsApi.getCards(params.request, params?.signal));
const addCardsEffect = createEffect(async (params: IQueryParams<AddUserCardsRequest>) => {
  const user = await UsersApi.addUserCards(params.request, params?.signal);
  if (!user.success) throw new Error(user.message || 'Error adding cards');
  const userIds = params.request as AddUserCardsRequest;
  return { AssignedUserId: userIds.userIds };
});
const deleteCardEffect = createEffect(async (params: IQueryParams<DeleteUserCardsRequest>) => {
  const user = await UsersApi.deleteUserCards(params.request, params?.signal);
  if (!user.success) throw new Error(user.message || 'Error deleting card');
  const userIds = params.request as DeleteUserCardsRequest;
  return { AssignedUserId: userIds.userIds };
});
const clearEvent = createEvent();
sample({
  source: addCardsEffect.doneData,
  target: getUserCardsEffect,
  fn: (doneData) => {
    return {
      request: {
        AssignedUserId: doneData.AssignedUserId,
      },
    };
  },
});
sample({
  source: deleteCardEffect.doneData,
  target: getUserCardsEffect,
  fn: (doneData) => {
    return {
      request: {
        AssignedUserId: doneData.AssignedUserId,
      },
    };
  },
});

const $dataStore = createStore<{ total: number; data: CardResponse[] }>({ total: 0, data: [] })
  .on(getUserCardsEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserCardsEffect.pending, (_, data) => data)
  .on(getUserCardsEffect.fail, (_, data) => false)
  .on(getUserCardsEffect.done, (_, data) => false)
  .on(addCardsEffect.pending, (_, data) => data)
  .on(addCardsEffect.fail, (_, data) => false)
  // .on(addCardsEffect.done, (_, data) => false)
  .on(deleteCardEffect.pending, (_, data) => data)
  .on(deleteCardEffect.fail, (_, data) => false)
  // .on(deleteCardEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserCardsEffect, () => null)
  .on(getUserCardsEffect.fail, (_, data) => data.error.message)
  .on(getUserCardsEffect.done, (_, data) => null)
  .on(addCardsEffect, () => null)
  .on(addCardsEffect.fail, (_, data) => data.error.message)
  .on(addCardsEffect.done, (_, data) => null)
  .on(deleteCardEffect, () => null)
  .on(deleteCardEffect.fail, (_, data) => data.error.message)
  .on(deleteCardEffect.done, (_, data) => null)
  .reset(clearEvent);
$errorStore.watch((x) => ApiUtils.errorStoreActionForbiddenWatcher(x, $notificationStore.events.setError));

export const $userCardsStore = {
  events: {
    getUserCards: ApiUtils.wrapperApi(getUserCardsEffect, RequestStrategyEnum.Debounce),
    addCards: ApiUtils.wrapperApi(addCardsEffect, RequestStrategyEnum.Debounce),
    deleteCard: ApiUtils.wrapperApi(deleteCardEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
